import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { PlaybookSortColumn } from "src/app/components/playbooks/playbooks-list/playbooks-list.component";
import { Playbook, PlaybookStatus } from "src/app/models/api/playbooks/playbook.model";
import { PlaybookCreationApiModel } from "src/app/models/api/playbooks/playbookCreationApiModel";
import { PlaybookUpdateApiModel } from "src/app/models/api/playbooks/playbookUpdateApiModel";
import { ISelectedFilters } from "src/app/models/filters/selectedFilters.interface";
import { LoggerService } from "../../infra/logger/logger.service";
import { HttpService } from "../http/http.service";
import { dateToUtcIsoString } from "src/app/common/datetime_utility";
import moment from "moment";

@Injectable({
    providedIn: "root",
})
export class PlaybooksHttpServiceService {
    public readonly defaultPageSize = 50;
    private readonly _baseUrl = "playbooks";

    constructor(
        private _httpService: HttpService,
        private _logger: LoggerService,
    ) {}

    /**
     * Get default violation rules
     * @returns A collection of default violation rules
     */
    public getPlaybooks(filterOptions?: ISelectedFilters, sort?: PlaybookSortColumn[]): Observable<Playbook[]> {
        const httpParams = this._createQueryParams(filterOptions, sort, true);

        return this._httpService.get<Playbook[]>(this._baseUrl, undefined, httpParams);
    }

    /**
     * Get default violation rules
     * @returns A collection of default violation rules
     */
    public getPlaybook(id: string): Observable<Playbook> {
        const url = `${this._baseUrl}/${id}`;

        return this._httpService.get<Playbook>(url);
    }

    /**
     * Create a new playbook
     */
    public createPlaybook(newPlaybookModel: PlaybookCreationApiModel): Observable<string> {
        return this._httpService.post<PlaybookCreationApiModel, string>(this._baseUrl, newPlaybookModel);
    }

    /**
     * Update a new playbook
     */
    public updatePlaybook(playbookId: string, playbookUpdateModel: PlaybookUpdateApiModel): Observable<string> {
        const url = `${this._baseUrl}/${playbookId}`;

        return this._httpService.put<string, PlaybookUpdateApiModel>(url, playbookUpdateModel);
    }

    /**
     * Disable a playbook
     */
    public playbookStatusUpdate(playbookId: string, newStatus: PlaybookStatus): Observable<string> {
        const url = `${this._baseUrl}/${playbookId}/status`;

        return this._httpService.put<string, { status: PlaybookStatus }>(url, {
            status: newStatus,
        });
    }

    /**
     * Delete a playbook
     */
    public deletePlaybook(playbookId: string): Observable<string> {
        const url = `${this._baseUrl}/${playbookId}`;

        return this._httpService.delete<string>(url);
    }

    private _createQueryParams(filterOptions?: ISelectedFilters, sortData?: PlaybookSortColumn[], shouldLimitResultsAmount = false): HttpParams {
        let httpParams = new HttpParams();

        if (shouldLimitResultsAmount) {
            httpParams = httpParams.append("pageSize", this.defaultPageSize);
        }

        if (filterOptions) {
            httpParams = this._appendFilterQueryParams(httpParams, filterOptions);
        }

        if (sortData) {
            httpParams = this._appendSortQueryParams(httpParams, sortData);
        }
        return httpParams;
    }

    private _appendFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
        if (filterOptions.integration && filterOptions.integration.length > 0) {
            httpParams = httpParams.append("integrationIds", filterOptions.integration.join(","));
        }

        if (filterOptions.platform && filterOptions.platform.length > 0) {
            httpParams = httpParams.append("platforms", filterOptions.platform.join(","));
        }

        if (filterOptions.playbookStatus && filterOptions.playbookStatus.length > 0) {
            httpParams = httpParams.append("playbookStatus", filterOptions.playbookStatus.join(","));
        }

        if (filterOptions.executionCalculatedStatus && filterOptions.executionCalculatedStatus.length > 0) {
            httpParams = httpParams.append("executionCalculatedStatus", filterOptions.executionCalculatedStatus.join(","));
        }

        if (filterOptions.lastExecutionSince) {
            httpParams = httpParams.append(
                "lastExecutionAtSince",
                dateToUtcIsoString(moment(filterOptions.lastExecutionSince).startOf("day").toDate()),
            );
        }

        if (filterOptions.lastExecutionUntil) {
            httpParams = httpParams.append(
                "lastExecutionAtUntil",
                dateToUtcIsoString(moment(filterOptions.lastExecutionUntil).endOf("day").toDate()),
            );
        }

        return httpParams;
    }

    private _appendSortQueryParams(httpParams: HttpParams, sortData: PlaybookSortColumn[]): HttpParams {
        const sortArray: string[] = [];

        sortData.forEach((sort) => {
            const sortFieldString: string | undefined = sort.key;
            const ascOrDescString = sort.isDesc ? "desc" : "asc";
            if (sortFieldString) {
                sortArray.push(`${sortFieldString}(${ascOrDescString})`);
            } else {
                this._logger.error(`Sort violation column ${sort.key} was not implemented`);
            }
        });
        return httpParams.append("sortBy", `[${sortArray}]`);
    }
}
